import { useQuery } from "@tanstack/react-query";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useProject } from "@/hooks/Network/useProject";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { OnboardingGroups, type OnboardingGroupsProps } from "./OnboardingGroups";

export function OnboardingGroupsPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t("page.onboarding.groups.title"));
  const project = useProject();
  const communityGroupQueries = useCommunityGroupQueries();

  const query = {
    groupTypes: ["interest", "realEstate"],
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;

  const { data: groups, isLoading, error } = useQuery(communityGroupQueries.list(query));

  const followMutation = communityGroupMutations.useFollowGroup(query);
  const unfollowMutation = communityGroupMutations.useUnfollowGroup(query);

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const groupsProps: OnboardingGroupsProps = {
    project,
    groups: groups ?? [],
    total: groups?.length ?? 0,
    onFollow: followMutation.mutate,
    onUnfollow: unfollowMutation.mutate,
  };

  return <OnboardingGroups {...groupsProps} />;
}
